import axios, { AxiosError } from "axios";
// We need a way to call the refresh token API.
// This will be defined in auth.ts, so we'll import it.
// This creates a slight awkwardness as auth.ts imports apiClient from this file (or base.ts).
// A more robust solution might involve an event bus or dependency injection for the refresh handler.
// For now, we'll assume authApi can be imported and used carefully.
import { authApi } from "./auth"; // Import authApi for the refresh token call

export const API_BASE_URL = import.meta.env.VITE_BASE_URL;
export const ENVIRONMENT = import.meta.env.VITE_ENV;

export const API_FEATURES = {
  USE_REAL_API: true,
  DEBUG_LOGS: import.meta.env.VITE_DEBUG_LOGS === "true",
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: 60000, // Increased timeout to 60 seconds
});

// Module-level variable to hold the current access token
let currentAccessToken: string | null = null;
let currentRefreshToken: string | null = null;
let authRefreshHandler: (() => Promise<string | null>) | null = null; // New: To hold the refresh handler from AuthContext

// Function to set tokens from AuthContext
export const setAuthTokens = (
  accessToken: string | null,
  refreshToken: string | null
) => {
  currentAccessToken = accessToken;
  currentRefreshToken = refreshToken;
  if (API_FEATURES.DEBUG_LOGS) {
    console.log(
      `[API Client] Tokens updated. Access: ${
        accessToken ? accessToken.substring(0, 10) + "..." : "none"
      }, Refresh: ${
        refreshToken ? refreshToken.substring(0, 10) + "..." : "none"
      }`
    );
  }
};

// New: Function to set the refresh handler from AuthContext
export const setAuthRefreshHandler = (
  handler: () => Promise<string | null>
) => {
  authRefreshHandler = handler;
  if (API_FEATURES.DEBUG_LOGS) {
    console.log("[API Client] Auth refresh handler set.");
  }
};

apiClient.interceptors.request.use((config) => {
  if (
    config.method === "post" ||
    config.method === "put" ||
    config.method === "patch"
  ) {
    if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }
  }

  // Use the module-level currentAccessToken
  if (currentAccessToken) {
    config.headers.Authorization = `Bearer ${currentAccessToken}`;
    if (API_FEATURES.DEBUG_LOGS) {
      console.log(
        `[API Client] Sending request to ${
          config.url
        } with Authorization: Bearer ${currentAccessToken.substring(0, 10)}...`
      );
    }
  } else {
    if (API_FEATURES.DEBUG_LOGS) {
      console.log(
        `[API Client] Sending request to ${config.url} without Authorization header.`
      );
    }
  }
  if (API_FEATURES.DEBUG_LOGS) {
    console.log(
      `[API Client] Request Interceptor - Final Authorization header for ${
        config.url
      }: ${
        typeof config.headers.Authorization === "string"
          ? config.headers.Authorization.substring(0, 20) + "..."
          : "none"
      }`
    );
  }
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    if (API_FEATURES.DEBUG_LOGS) {
      console.log(
        `[API Client] Response Interceptor: Received error for ${error.config?.url}. Status: ${error.response?.status}.`
      );
      if (error.response?.data) {
        console.log(
          "[API Client] Response Interceptor: Error data:",
          error.response?.data
        );
      }
      console.log(
        `[API Client] Response Interceptor: Checking for auth error. Status: ${
          error.response?.status
        }, Message: ${(error.response?.data as { message: string })?.message}`
      );
    }

    // Check if it's a 401 error or a 500 error with "Token expired" message, and not a retry request or refresh token request
    const isAuthError =
      (error.response?.status === 401 &&
        originalRequest &&
        !(originalRequest as any)._retry) ||
      (error.response?.status === 500 &&
        (error.response?.data as { message: string })?.message ===
          "Token expired" &&
        originalRequest &&
        !(originalRequest as any)._retry);

    if (isAuthError) {
      if (API_FEATURES.DEBUG_LOGS) {
        console.log(
          "[API Client] Response Interceptor: Auth error detected. Attempting refresh."
        );
        console.log(
          `[API Client] Response Interceptor: authRefreshHandler is ${
            authRefreshHandler ? "set" : "NOT set"
          }.`
        );
      }
      if (isRefreshing) {
        // If already refreshing, add to queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers["Authorization"] = "Bearer " + token;
            }
            console.log(
              `[API Client] Retrying original request with new token: ${
                originalRequest.headers?.Authorization
                  ? (originalRequest.headers.Authorization as string).substring(
                      0,
                      20
                    ) + "..."
                  : "none"
              }`
            );
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      (originalRequest as any)._retry = true;
      isRefreshing = true;

      // Use the provided authRefreshHandler
      if (!authRefreshHandler) {
        isRefreshing = false;
        console.error("[API Client] Auth refresh handler not set.");
        // Fallback to direct logout if handler isn't available
        setAuthTokens(null, null);
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        if (window.location.pathname !== "/login")
          window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const newAccessToken = await authRefreshHandler(); // Call the handler from AuthContext

        if (newAccessToken) {
          if (originalRequest.headers) {
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;
          }
          processQueue(null, newAccessToken);
          return apiClient(originalRequest);
        } else {
          // Refresh failed within AuthContext, it should have handled logout
          processQueue(new Error("Token refresh failed via AuthContext"), null);
          return Promise.reject(error);
        }
      } catch (refreshError: any) {
        console.error(
          "[API Client] Token refresh failed via AuthContext:",
          refreshError
        );
        processQueue(refreshError, null);
        // AuthContext's handleRefreshToken should already clear data and redirect
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    // For other errors, or if it's a 403, just reject
    if (error.response?.status === 403) {
      if (error.config?.url?.includes("/admin/")) {
        console.error("Access forbidden. User may not have admin privileges.");
      }
    }
    return Promise.reject(error);
  }
);

export const API_ENDPOINTS = {
  AUTH_LOGIN: `/api/auth/login`, // Relative to baseURL
  AUTH_REGISTER: `/api/auth/register`,
  AUTH_VERIFY_EMAIL: `/api/auth/verify-email`,
  AUTH_RESEND_VERIFICATION: `/api/auth/resend-verification-code`,
  AUTH_FORGOT_PASSWORD: `/api/auth/forgot-password`,
  AUTH_RESET_PASSWORD: `/api/auth/reset-password`,
  AUTH_REFRESH_TOKEN: `/api/auth/refresh-token`,
  AUTH_LOGOUT: `/api/auth/logout`,
  AUTH_GOOGLE: `/api/auth/google`,
  // ... other endpoints
  USER_PROFILE: (userId: string | number) => `/api/users/${userId}`,
};

export default apiClient; // Exporting the configured apiClient
